// How to use:
// v-if="$user.hasRoles([ROLES.organization, ROLES.operator])"
// v-if="$user.isAuthenticated"
// h1 {{ $user.data.username }}

import store from '@/store/index';
import CertificateProfile from '@/models/list/certificate-profile';
import OrganizerProfile from '@/models/list/organizer-profile';
import { ROLES } from '@/constants/roles';
import { isVcht } from '@/constants/global';
import UserLoginInfo from '@/models/list/user/login-info';

export default class User {
  constructor() {
    this.store = store;
  }

  get data() {
    return this.store.getters['auth/getUserData'];
  }

  get isAuthenticated() {
    return this.store.getters['auth/isAuthenticated'];
  }

  /**
   * Временное решение для ВЦХТ
   * Отдает значение true для проверенных организаций, которым разрешено создание мероприятия
   * @return boolean
   */
  get isVchtAllowedOrganization() {
    if (!isVcht) {
      return true;
    }

    return this.store.getters['auth/whitelistVchtOrganizations'].map(({ value }) => value).includes(this.data.id);
  }

  get isPfdoCreated() {
    return this.data.is_pfdo_user;
  }

  get roles() {
    return this.store.getters['auth/getUserRolesNew'];
  }

  get permissions() {
    return this.data?.permissions || [];
  }

  get role() {
    const [firstRole] = this.roles;

    return firstRole;
  }

  get fio() {
    return this.data;
  }

  get attributes() {
    return this.store.getters['auth/getUserAttributes'];
  }

  get filteredAttributes() {
    return this.store.getters['auth/getFilteredUserAttributes'];
  }

  get ageAttribute() {
    return this.store.getters['auth/getAgeUserAttribute'];
  }

  hasRoles(roles = []) {
    const allowedRoles = this.roles?.filter(({ name }) => (roles || []).includes(name));

    return allowedRoles?.length || 0;
  }

  hasConnectedRole(role) {
    return !!this.getConnectedRoleUser(role);
  }

  getConnectedRoleUser(role) {
    return (this.connectedUsers || []).find((user) => {
      const roles = user?.roles || [];
      const hasRoleInList = roles.some(({ name }) => name === role);

      return hasRoleInList || user?.role === role;
    }) || null;
  }

  /**
   * Check permissions if it exists in user permissions
   * @param {Array} permissions - original list for checking
   * @param {Object} options - values for searching current permissions from user list
   * @param {Object} settings - @params (list) or @all (boolean)
   * @returns {Boolean}
   */
  hasPermissions(permissions, options = {}, settings = {}) {
    const params = settings?.params || ['event_id', 'event_nomination_id'];
    const all = settings?.all ?? true;

    const currentData = this.permissions.find((item) =>
      params.reduce((acc, key) => (options[key] || null) === (item[key] || null) && acc, true),
    );
    const allowedPermissions = (currentData?.permissions || []).filter((permission) => permissions.includes(permission));

    return all ? allowedPermissions.length === permissions.length : !!allowedPermissions.length;
  }

  hasPermissionsWithManyOptions(permissions, optionsList = [], settings = {}) {
    const all = settings?.all ?? true;
    const result = optionsList.map((options) => this.hasPermissions(permissions, options, settings));
    // console.log(permissions, all, result, result.some(Boolean));

    return all ? result.every(Boolean) : result.some(Boolean);
  }

  get isCertificate() {
    const { certificate } = ROLES;

    return !!(this.isAuthenticated && this.hasRoles([certificate]));
  }

  get isMember() {
    const { member, certificate } = ROLES;

    return !!(this.isAuthenticated && this.hasRoles([member, certificate]));
  }

  get isOrganization() {
    return this.store.getters['auth/isOrganization'];
  }

  get isExpert() {
    return this.store.getters['auth/isExpert'];
  }

  get isTrainer() {
    return this.store.getters['auth/isTrainer'];
  }

  get isEventManager() {
    return this.store.getters['auth/isEventManager'];
  }

  get connectedUsers() {
    return this.data?.connected_users || [];
  }

  get connectedToUser() {
    return this.data?.connected_to_user || null;
  }

  get hasConnectedExpert() {
    return this.hasConnectedRole(ROLES.expert);
  }

  get hasConnectedTrainer() {
    return this.hasConnectedRole(ROLES.trainer);
  }

  get hasConnectedEventManager() {
    return this.hasConnectedRole(ROLES.event_manager);
  }

  get eventManagerOrganizer() {
    return this.getProfileModel(this.data?.organizer || {});
  }

  get isOperator() {
    return this.store.getters['auth/isOperator'];
  }

  get isDepartment() {
    return this.store.getters['auth/isDepartment'];
  }

  get isModerator() {
    return this.store.getters['auth/isModerator'];
  }

  get isAdmin() {
    return this.store.getters['auth/isAdmin'];
  }

  get profile() {
    if (!this.profileField) {
      return null;
    }

    return this.getProfileModel(this.data[this.profileField]);
  }

  get profileField() {
    // Роли без профиля
    if (this.hasRoles([ROLES.operator, ROLES.department, ROLES.admin, ROLES.moderator])) {
      return null;
    }

    if (this.hasRoles([ROLES.organization])) {
      return 'organizer';
    }

    return 'profile';
  }

  getProfileModel(item) {
    if (this.hasRoles([ROLES.organization])) {
      return new OrganizerProfile(item);
    }

    return new CertificateProfile(item || {});
  }

  /**
   *  ! @FIX temporary
   *  Requirements = {
   *    is_region_required: boolean;
   *    is_municipality_required: boolean;
   *    is_first_name_required: boolean;
   *    is_last_name_required: boolean;
   *    is_birthday_at_required: boolean;
   *    is_snils_required: boolean;
   *  };
   */
  checkProfileForRequirements(requirements) {
    const needFillRegion = requirements.is_region_required && !this.profile.region;
    const needFillMunicipality = requirements.is_municipality_required && !this.profile.municipality;
    const needFillDirectory = needFillRegion || needFillMunicipality;
    const needFillFirstName = requirements.is_first_name_required && !this.profile.first_name;
    const needFillLastName = requirements.is_last_name_required && !this.profile.last_name;
    const needFillBirthday = requirements.is_birthday_at_required && !this.profile.birthday_at;
    const needFillSnils = requirements.is_snils_required && !this.profile.snils;

    return [needFillDirectory, needFillFirstName, needFillLastName, needFillBirthday, needFillSnils].every((value) => value === false);
  }

  get loginInfo() {
    const info = this.store.getters['auth/getUserData']?.login_info;
    return info?.id ? new UserLoginInfo(info) : undefined;
  }

  /**
   * Mutates userData in vuex
   * @params {Object} Array of user data storage properties, e.g { timezone: '-7 UTC' }
   */
  patch(params) {
    this.store.dispatch('auth/patchUser', params);
  }

  patchProfile(params) {
    this.store.dispatch('auth/patchUser', { [this.profileField]: this.getProfileModel({ ...this.profile, ...params }) });
  }

  reloadUser() {
    return this.store.dispatch('auth/reloadUser');
  }

  get deleteAccount() {
    return {
      submit: () => this.store.dispatch('auth/deleteAccount'),
      cancel: () => this.store.dispatch('auth/deleteAccountCancel'),
      request: this.data.user_delete_request,
    };
  }
}
